/**
 * The landing page's sound switch.
 *
 * Janeks: *"mute button"* in the header, and *"Will have a scene sound added
 * later"* for the hero. So the ambience does not exist yet and the control for
 * it does.
 *
 * ─── The button is LIVE, not disabled ───────────────────────────────────────
 * Same call `bootstrap.ts` makes for the settings gear that has no screen
 * behind it: a `disabled` button swallows pointer events in Chrome, so it
 * cannot even show a tooltip, and a control that greys itself out reads as
 * broken rather than as pending. This one really does toggle a real, persisted
 * preference — it simply has nothing to apply it to yet, and the moment
 * `registerAmbience` is handed an element the preference is already there and
 * correct, including for a visitor who muted the page months earlier.
 *
 * ─── It plays the settlement's own score ───────────────────────────────────
 * `TRACKS.settlementAmbient` — the same bed the game plays while you are in
 * New Ashford — through the same `createMixer()` the game uses, on the same
 * `music` channel. The landing page is the settlement, so it is the same
 * music; there is no second asset and no second mixer.
 *
 * It was a preference with nothing behind it for two batches, because there
 * was no audio in this game at all. There is now.
 *
 * ─── The browser decides when it may start ─────────────────────────────────
 * An `AudioContext` created before any user gesture is born `suspended`, and
 * `resume()` only succeeds from inside a gesture handler. So unmuting is
 * necessarily the thing that starts the music: the visitor clicking the
 * speaker IS the gesture. Muted-by-default is not a workaround for that — see
 * below — but it does mean the two line up.
 *
 * ─── It is still NOT the game's audio settings ─────────────────────────────
 * `engine/state/audio-settings.ts` is the three-channel mixer behind the
 * in-game Settings screen, stored under `ah.audio-settings`. This is one
 * boolean for one loop on a page with no account and no settings screen. They
 * should converge on that store's `master.muted` when the landing page has an
 * account to read a preference from; today it does not, and reaching into a
 * store the page cannot write back to would be worse than the duplication.
 *
 * ─── Muted by default, and that is not a placeholder ────────────────────────
 * Browsers block autoplay with sound, and a landing page that starts talking
 * at you is a landing page people close. The stored value only ever records a
 * DEPARTURE from that default, so clearing site data returns to silence rather
 * than to whatever the last visitor chose.
 */

import { TRACKS } from '../audio/tracks';
import { createMixer, type MixerHandle } from '../audio/mixer';

const STORAGE_KEY = 'ah:landing:muted';

/**
 * How loud the bed sits on the landing page.
 *
 * Below the game's own level. This is background to reading, not to playing,
 * and a visitor who unmutes out of curiosity should not be startled.
 */
const MUSIC_GAIN = 0.55;
const FADE_SECONDS = 1.4;

type Listener = (muted: boolean) => void;

const listeners = new Set<Listener>();
let ambience: HTMLMediaElement | null = null;

/** The shared mixer, built on the first unmute — never before a gesture. */
let mixer: MixerHandle | null = null;
/** Stops and fades the bed. Null when nothing is playing. */
let stopMusic: (() => void) | null = null;
/** Guards against a second start while the first is still loading. */
let starting = false;
/** Document listeners for the interface sounds, bound once. */
let sfxBound = false;
/** Removes the one-shot gesture listeners that resume a stored preference. Null when not armed. */
let disarmGesture: (() => void) | null = null;
/** Last play, for the retrigger guard. */
let lastSfx = -Infinity;

function readStored(): boolean {
  try {
    // Only the literal 'false' unmutes. Anything else — absent, corrupt, a
    // value written by a future version — falls back to silence.
    return window.localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    // Private mode / disabled storage. Not an error worth surfacing on a
    // landing page; the preference simply does not persist.
    return true;
  }
}

let muted = readStored();

export function isMuted(): boolean {
  return muted;
}

export function setMuted(next: boolean): void {
  if (next === muted) return;
  muted = next;
  // A visitor who mutes before ever clicking anything has answered the
  // question the armed listener was waiting to ask.
  if (muted) disarmGesture?.();
  try {
    window.localStorage.setItem(STORAGE_KEY, String(next));
  } catch { /* see readStored */ }
  applyToAmbience();
  applyToMusic();
  for (const fn of listeners) fn(muted);
}

/**
 * Starts or stops the settlement bed.
 *
 * Called from `setMuted`, which is called from a click handler, which is what
 * makes the `AudioContext` legal to resume. Everything here is best-effort: a
 * browser that refuses to start audio, a missing file or a decode failure
 * leaves the page exactly as it was, with the button still reflecting the
 * preference the visitor expressed.
 */
function applyToMusic(): void {
  if (muted) {
    stopMusic?.();
    stopMusic = null;
    return;
  }
  if (stopMusic || starting) return;
  starting = true;
  try {
    mixer = mixer ?? createMixer();
  } catch (err) {
    starting = false;
    console.warn('[landing] no audio context available', err);
    return;
  }
  bindUiSounds();
  void mixer.playLoop(TRACKS.settlementAmbient.path, 'music', MUSIC_GAIN, FADE_SECONDS)
    .then((stop) => {
      starting = false;
      // Muted again while the clip was still decoding — do not start a loop
      // nobody asked for any more.
      if (muted) { stop(); return; }
      stopMusic = stop;
    })
    .catch((err) => {
      starting = false;
      console.warn('[landing] could not start the settlement bed', err);
    });
}

export function toggleMuted(): boolean {
  setMuted(!muted);
  return muted;
}

/**
 * Honours a stored "unmuted" on a fresh page load.
 *
 * Without this the preference was write-only: `muted` was read from storage at
 * import time, the header painted the speaker-on icon from it, and nothing
 * ever called `applyToMusic` — because `setMuted` early-returns on an
 * unchanged value and it is the ONLY caller. So a returning visitor saw sound
 * ON and heard nothing, and the toggle appeared to work only because muting
 * and unmuting again finally produced a change.
 *
 * It waits for a gesture rather than starting on load, and the wait is the
 * point: the code path a click on the speaker takes is the one we know works
 * in every browser — context resumed and source started inside the gesture.
 * Scheduling a source onto a still-suspended context and trusting the mixer's
 * own unlock listener to rescue it is a different path, exercised nowhere
 * else in this app, and Safari is exactly where it would fail silently.
 *
 * The listeners are module-level and bound at most once: `wireChrome` calls
 * this on every mount, and it remounts on every SPA navigation between the
 * landing page and News.
 */
export function armStoredPlayback(): void {
  if (muted || disarmGesture || stopMusic || starting) return;
  const events: (keyof DocumentEventMap)[] = ['pointerdown', 'keydown', 'touchstart'];
  const onGesture = (): void => {
    disarmGesture?.();
    // Mid-gesture mute: `pointerdown` on the speaker button lands before its
    // `click`, so this can run while the visitor is in the act of silencing
    // the page. `applyToMusic` re-checks `muted` once the clip has decoded and
    // drops the loop, so the worst case is a decode nobody hears.
    if (!muted) applyToMusic();
  };
  disarmGesture = () => {
    disarmGesture = null;
    for (const e of events) document.removeEventListener(e, onGesture, true);
  };
  for (const e of events) document.addEventListener(e, onGesture, { capture: true, passive: true });
}

/**
 * Hands the audio over to the game.
 *
 * `startBaseView` builds its own mixer and plays the SAME settlement bed at
 * full gain, and it binds its own click sounds — so leaving this one running
 * after login means two copies of one loop on two contexts, phasing against
 * each other, plus a doubled click. The preference and its subscribers are
 * left alone: signing out returns to the landing page with the speaker still
 * as the visitor set it, and the next gesture starts the bed again.
 */
export function stopLandingAudio(): void {
  disarmGesture?.();
  stopMusic?.();
  stopMusic = null;
  mixer?.dispose();
  mixer = null;
}

/** Fires immediately with the current value, then on every change. Returns an unsubscribe. */
export function subscribeMuted(fn: Listener): () => void {
  listeners.add(fn);
  fn(muted);
  return () => { listeners.delete(fn); };
}

/**
 * Hands the switch something to switch.
 *
 * Call it when the hero ambience lands; pass `null` on teardown. Playback is
 * attempted only when unmuted, and a rejected `play()` is swallowed: an
 * autoplay block is the browser working as intended, not a fault to log.
 */
export function registerAmbience(el: HTMLMediaElement | null): void {
  ambience = el;
  applyToAmbience();
}

function applyToAmbience(): void {
  if (!ambience) return;
  ambience.muted = muted;
  if (muted) {
    ambience.pause();
    return;
  }
  void ambience.play().catch(() => { /* autoplay policy — nothing to do */ });
}

/**
 * Minimum gap between two interface sounds, ms. Matches the game's own
 * `UI_RETRIGGER_MS`: a click that both blurs an input and presses a button
 * fires twice in the same tick, and two strikes a millisecond apart read as a
 * fault rather than as feedback.
 */
const SFX_RETRIGGER_MS = 120;

/**
 * A click on anything pressable, and a focus landing in a field.
 *
 * Janeks: *"use the UI click sounds on button clicks and 'active' elements
 * (e.g. clicking into inputs)."*
 *
 * The game already has `createClickSound` (`engine/audio/ui-sounds.ts`) and it
 * is not reusable here for two reasons: it matches `button,[role="button"],
 * [data-choice]` only — every call to action on this page is an `<a>` — and it
 * is mounted by the settlement view, which does not exist on a public page.
 * Same track, same mixer, same throttle; a wider selector and a focus rule.
 *
 * Bound ONCE, on the first unmute, and never unbound. Two passive
 * document-level listeners cost nothing measurable, and the alternative —
 * lifecycle-managing them across the landing page, the news pages and six auth
 * screens — is a pile of ways to leak or double-bind for no gain. They check
 * `muted` on every event, so silencing the page silences them immediately.
 */
function bindUiSounds(): void {
  if (sfxBound) return;
  sfxBound = true;

  const play = (): void => {
    if (muted || !mixer || !mixer.isRunning()) return;
    const now = performance.now();
    if (now - lastSfx < SFX_RETRIGGER_MS) return;
    lastSfx = now;
    const track = TRACKS.uiClick;
    // Never throw out of a DOM listener.
    void mixer.playOnce(track.path, track.channel).catch(() => {});
  };

  document.addEventListener('click', (e) => {
    const el = e.target as HTMLElement | null;
    // Links as well as buttons — the primary call to action on this page is an
    // anchor, and the policy links open a sheet rather than navigating.
    if (!el?.closest?.('button,a[href],[role="button"],summary')) return;
    play();
  }, { capture: true, passive: true });

  // `focusin`, not `focus`: focus does not bubble, and a delegated listener is
  // the only way to cover fields that are injected after this is bound.
  document.addEventListener('focusin', (e) => {
    const el = e.target as HTMLElement | null;
    if (!el?.matches?.('input,textarea,select')) return;
    // A checkbox or radio is also a click, and the click listener above will
    // already have sounded for it — the throttle absorbs the duplicate, but
    // skipping it outright keeps the two rules from fighting over the same
    // gesture.
    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) return;
    play();
  }, { capture: true, passive: true });
}

/** Is the bed actually playing? For probes and tests. */
export function musicIsPlaying(): boolean {
  return stopMusic !== null;
}

/** Test seam: drops every listener, the registered element and the mixer. */
export function resetLandingAudio(): void {
  listeners.clear();
  disarmGesture?.();
  ambience = null;
  stopMusic?.();
  stopMusic = null;
  starting = false;
  mixer?.dispose();
  mixer = null;
  muted = readStored();
}
